// Auth context for RTLSMobile web.
//
// Mirrors TagBoard/web-vite/src/app/useAuth.tsx — same surface, same patterns,
// same public.users + personnel lookup. The two clients share Supabase so a
// dispatcher logging in on TagBoard web has the same identity when they
// open RTLS web.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

export interface UserProfile {
  id: string
  display_name: string | null
  role: 'admin' | 'dispatcher' | 'supervisor' | 'worker'
  personnel_id: string | null
  personnel_name: string | null
}

interface AuthContextValue {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    // Resolve cached session on mount. If the stored refresh token is dead
    // (revoked, expired by 60-day inactivity, or wiped server-side after a
    // JWT-secret rotation) supabase-js throws AuthApiError("Invalid Refresh
    // Token: Refresh Token Not Found"). Clear the local copy so AuthGate
    // drops the user to LoginScreen instead of looping the error on every
    // auto-refresh tick.
    ;(async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (cancelled) return
        if (error) {
          console.warn('[Auth] Stale session, signing out locally:', error.message)
          await supabase.auth.signOut({ scope: 'local' })
          setSession(null)
        } else {
          setSession(data.session)
        }
      } catch (err: any) {
        console.warn('[Auth] Session resolve failed:', err?.message ?? err)
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
        if (!cancelled) setSession(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      // TOKEN_REFRESHED with a null session means the auto-refresher gave
      // up — wipe the local copy so we don't keep retrying a dead token.
      if (event === 'TOKEN_REFRESHED' && !next) {
        supabase.auth.signOut({ scope: 'local' }).catch(() => {})
      }
      setSession(next)
    })
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  // Load the app-level profile whenever the active user id changes.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) {
      setProfile(null)
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase
        .from('users' as any)
        .select('id, display_name, role, personnel_id, personnel:personnel_id(name)')
        .eq('id', userId)
        .maybeSingle()
      if (cancelled) return
      if (error || !data) {
        setProfile({
          id: userId,
          display_name: null,
          role: 'dispatcher',
          personnel_id: null,
          personnel_name: null,
        })
        return
      }
      const personnel = Array.isArray((data as any).personnel)
        ? (data as any).personnel[0]
        : (data as any).personnel
      setProfile({
        id: (data as any).id,
        display_name: ((data as any).display_name as string | null) ?? null,
        role: (((data as any).role as string) ?? 'dispatcher') as UserProfile['role'],
        personnel_id: ((data as any).personnel_id as string | null) ?? null,
        personnel_name: personnel?.name ?? null,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [session?.user?.id])

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error?: string }> => {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) return { error: error.message }
      return {}
    },
    [],
  )

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
